import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCar } from 'react-icons/fa';
import {
  useToaster,
  Uploader,
  Message,
  Loader,
  Stack,
  Form,
  SelectPicker,
  DatePicker,
  Divider,
  Radio,
  InputPicker,
  Checkbox,
  Schema,
  IconButton,
  Whisper,
  Popover,
  Toggle,
  Modal,
  Button,
  Panel,
  PanelGroup,
  TagPicker
} from 'rsuite';
import { Icon, Plus } from '@rsuite/icons';
import AvatarIcon from '@rsuite/icons/legacy/Avatar';
import { VscSave } from 'react-icons/vsc';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import {
  getUserByID,
  getUserBalance,
  updateUser,
  getAdminUsers,
  getManagers,
  blockUser
} from '@/services/user.service';
import { useParams } from 'react-router-dom';
import { getGrades } from '@/services/grade.service';
import { getServicelines } from '@/services/serviceline.service';
import { getWfhProfiles } from '@/services/wfhProfile.service';
import { getDesks, updateAvailibility, updateDesk } from '@/services/desk.service';
import { getSpots } from '@/services/spot.service';
import { getAccessProfiles } from '@/services/accessProfile.service';
import { getCurrentUser } from '@/services/auth.service';
import { FaRegBuilding, FaRegUser } from 'react-icons/fa';
import { MdOutlineHomeWork } from 'react-icons/md';
import { LuUserCog } from 'react-icons/lu';
import { updateUserBalance } from '@/services/balance.service';

const { StringType } = Schema.Types;

const model = Schema.Model({
  name: StringType().isRequired('This field is required.'),
  grade: StringType().isRequired('This field is required.'),
  email: StringType()
    .isEmail('Please enter a valid email address.')
    .isRequired('This field is required.')
});

function previewFile(file, callback) {
  const reader = new FileReader();
  reader.onloadend = () => {
    callback(reader.result);
  };
  reader.readAsDataURL(file);
}

const UserForm = () => {
  const toaster = useToaster();
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const currentUser = getCurrentUser();

  const user = useAppSelector((state: any) => state.users.item);
  const loading = useAppSelector((state: any) => state.users.loading);
  const users = useAppSelector((state: any) => state.users.data)?.map(item => {
    return { value: item._id, label: item.firstname + ' ' + item.lastname };
  });
  const managers = useAppSelector((state: any) => state.users.managers) || [];
  const mappedManagers = Array.isArray(managers)
    ? managers.map(item => ({
        value: item._id,
        label: `${item.firstname} ${item.lastname}`
      }))
    : [];
  const filteredSecondManagers = mappedManagers.filter(
    manager => manager.value !== (userData?.manager?._id || userData?.manager)
  );

  const features = useAppSelector((state: any) => state.feature.data);
  const grades = useAppSelector((state: any) => state.grade.data)?.map(item => {
    return { value: item._id, label: item.grade_name };
  });
  const servicelines = useAppSelector((state: any) => state.serviceLine.data)?.map(item => {
    return { value: item._id, label: item.serviceLine, SubServiceLines: item.SubServiceLines };
  });
  const wfhProfiles = useAppSelector((state: any) => state.wfhProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });
  const desks = useAppSelector((state: any) => state.desk.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });
  const accessProfiles = useAppSelector((state: any) => state.accessProfile.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });
  const spots = useAppSelector((state: any) => state.spot.data)?.map(item => {
    return { ...item, value: item._id, label: item.name };
  });

  const [uploading, setUploading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [subservicelines, setSubservicelines] = useState([]);
  const [updatedData, setUpdatedData] = useState<any>({});
  const [userData, setUserData] = useState(user);
  const [extraBalance, setExtraBalance] = useState();
  const [oldExtraBalance, setOldExtraBalance] = useState();
  const [userBalance, setUserBalance] = useState();
  const [carNumber, setCarNumber] = useState('');

  const [resignationDate, setResignationDate] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState<any>({
    isOpen: false,
    title: '',
    subtitle: '',
    status: null
  });
  const [modules, setModules] = useState([]);
  const profiles = [
    { label: t('Facility Administrator'), value: 'facility' },
    { label: t('DAF Administrator'), value: 'daf' },
    { label: t('Super Administrator'), value: 'super' }
  ];

  useEffect(() => {
    dispatch(getUserByID(id));
    dispatch(getAdminUsers());
    dispatch(getGrades());
    dispatch(getManagers());
    dispatch(getServicelines());
    dispatch(getWfhProfiles());
    dispatch(getDesks());
    dispatch(getSpots());
    dispatch(getAccessProfiles());

    const fetchUserBalance = async () => {
      const result = await getUserBalance(id);
      setExtraBalance(result?.extraBalance);
      setOldExtraBalance(result?.extraBalance);
      setUserBalance(result);
    };

    fetchUserBalance();
  }, []);
  useEffect(() => {
    const featuresArray: any = [
      {
        value: 'organizationSettings',
        label: 'Members & Organization settings',
        name: 'Members & Organization settings'
      }
    ];

    const addedFeatures = new Set();

    features?.forEach(project => {
      const featureName = project.name?.toLowerCase();

      if (featureName === 'on site') {
        if (!addedFeatures.has('flexOffice')) {
          featuresArray.push({
            label: 'Flex Office',
            value: 'flexOffice'
          });
          addedFeatures.add('flexOffice');
        }
      }

      if (featureName === 'wfh') {
        if (!addedFeatures.has('remoteWork')) {
          featuresArray.push({
            label: 'Remote Work',
            value: 'remoteWork'
          });
          addedFeatures.add('remoteWork');
        }
      }

      if (featureName === 'leave') {
        if (!addedFeatures.has('Leave')) {
          featuresArray.push({
            label: 'Leave',
            value: 'leave'
          });

          addedFeatures.add('Leave');
        }
      }

      if (featureName === 'travel') {
        if (!addedFeatures.has('travel')) {
          featuresArray.push({
            label: 'Business Travel',
            value: 'travel'
          });
          addedFeatures.add('travel');
        }
      }
    });

    if (!addedFeatures.has('analytics')) {
      featuresArray.push({
        label: 'Analytics',
        value: 'analytics'
      });
      addedFeatures.add('analytics');
    }

    setModules(featuresArray);
  }, [features]);

  useEffect(() => {
    setUserData({
      ...user,
      isAdmin: user.admin !== null && user.admin != undefined,
      isValidator: user.role?.includes('validator'),
      isManager: user.role?.includes('manager'),
      photo: ''
    });
  }, [user]);

  useEffect(() => {
    if (userData?.serviceLine || updatedData.serviceLine) {
      const subservicelines = servicelines.find(
        item =>
          item.value == (updatedData.serviceLine ? updatedData.serviceLine : userData?.serviceLine)
      )?.SubServiceLines;
      const values = subservicelines?.map(item => {
        return { value: item, label: item };
      });
      setSubservicelines(values);
    }
  }, [userData]);

  const changeUser = (field, value) => {
    setUpdatedData(prevData => ({ ...prevData, [field]: value }));
    setUserData(prevData => ({ ...prevData, [field]: value }));
  };

  const handleBlock = status => {
    if (status) {
      setConfirmDialog({
        isOpen: true,
        title: t('Block User'),
        subtitle: true,
        status
      });
    } else {
      setConfirmDialog({
        isOpen: true,
        title: t('Are you sure you want to unblock this user?'),
        subtitle: (
          <div style={{ lineHeight: '1.5', marginBottom: 10 }}>
            {t('This account will be active')}
            <div style={{ marginTop: 5 }}>
              {userData.resignation_date
                ? t('Resignation date: {{date}}', {
                    date: new Date(userData.resignation_date)
                      .toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                      .replace(/\//g, '-')
                  })
                : t('Resignation date is not set')}
            </div>
          </div>
        ),
        status
      });
    }
  };

  const handleUpdateUserStatus = async () => {
    const status = confirmDialog.status;
    if (status && !resignationDate) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Please select a resignation date.')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    try {
      const formData = new FormData();
      formData.append('isBlocked', status.toString());
      if (status && resignationDate) {
        formData.append('resignation_date', resignationDate.toISOString());
      }

      await dispatch(updateUser(id, formData));
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t(
            status
              ? 'User blocked and resignation date set successfully'
              : 'User unblocked successfully'
          )}
        </Message>,
        { placement: 'topCenter' }
      );

      await dispatch(getUserByID(id));
      setResignationDate(null);
      setConfirmDialog({ isOpen: false, title: '', subtitle: '', status: null });
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message || 'Failed to update user status')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  const handleSave = async () => {
    console.log('Updated data before sending:', updatedData);
    if (Object.keys(updatedData).length === 0 && Number(extraBalance) == Number(oldExtraBalance)) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('No changes found')}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    const formData = new FormData();
    for (const key in updatedData) {
      if (updatedData.hasOwnProperty(key)) {
        const value = updatedData[key];
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(`${key}[]`, item));
        } else {
          formData.append(key, value);
        }
      }
    }

    try {
      if (Number(extraBalance) != Number(oldExtraBalance)) {
        await dispatch(updateUserBalance(userBalance?._id, { extraBalance: extraBalance }));
      }
      if (user.spot && updatedData.spot) {
        if (updatedData.spot != user.spot?._id) {
          await updateDesk(user.spot._id, { isBlocked: false });
          await updateAvailibility(user.spot._id, false);
          await updateDesk(updatedData.spot, { isBlocked: true });
          await updateAvailibility(updatedData.spot, true);
        } else if (user.spot && updatedData.spot == null) {
          await updateDesk(user.spot?._id, { isBlocked: false });
          await updateAvailibility(user.spot?._id, false);
        }
      }
      if (!user.spot && updatedData.spot && updatedData.spot != null) {
        await updateDesk(updatedData.spot, { isBlocked: true });
        await updateAvailibility(updatedData.spot, true);
      }
      if (
        userData.manager &&
        userData.secondValidator &&
        userData.manager === userData.secondValidator
      ) {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {t('Manager and Second Manager cannot be the same user')}
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }
      if (user.desk && updatedData.desk) {
        if (updatedData.desk && updatedData.desk != user.desk?._id) {
          await updateDesk(user.desk._id, { isBlocked: false });
          await updateAvailibility(user.desk._id, false);
          await updateDesk(updatedData.desk, { isBlocked: true });
          await updateAvailibility(updatedData.desk, true);
        } else if (user.desk && updatedData.desk == null) {
          await updateDesk(user.desk?._id, { isBlocked: false });
          await updateAvailibility(user.desk?._id, false);
        }
      }
      if (!user.desk && updatedData.desk && updatedData.desk != null) {
        await updateDesk(updatedData.desk, { isBlocked: true });
        await updateAvailibility(updatedData.desk, true);
      }
      if (userData.cars?.length === 0 && user.cars?.length > 0) {
        formData.append('cars[]', '');
      }
      console.log('FormData entries:', Object.fromEntries(formData.entries()));
      await dispatch(updateUser(user._id, formData));

      console.log('Update user response:', user._id);
      toaster.push(
        <Message type="success" duration={5000} showIcon>
          {t('Updated successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
      await dispatch(getUserByID(id));
    } catch (error) {
      console.error('Erreur lors de la soumission :', error);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message || 'Failed to update user')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  const speaker = (
    <Popover
      title={
        wfhProfiles.find(item => item.value == (userData.wfhProfile?._id || userData.wfhProfile))
          ?.label
      }
    >
      <p>{t('Authorized days:')} </p>
      {wfhProfiles
        .find(item => item.value == (userData.wfhProfile?._id || userData.wfhProfile))
        ?.authorizedWfhDays?.map((day, index) => (
          <p key={index}>{day}</p>
        ))}
    </Popover>
  );

  const renderMenuItem = (label, item) => {
    const speaker = (
      <Popover title={item.label || 'WFH Profile'}>
        <p>Authorized days:</p>
        {item.authorizedWfhDays?.length > 0 ? (
          item.authorizedWfhDays.map((day, index) => <p key={index}>{day}</p>)
        ) : (
          <p>{t('No authorized days')}</p>
        )}
      </Popover>
    );

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={speaker}
        controlId={`control-id-${item.value}`}
        key={item.value}
      >
        <div>{label}</div>
      </Whisper>
    );
  };

  const speakerAccess = (
    <Popover
      title={
        accessProfiles.find(item => item.value == (userData.Group?._id || userData.Group))?.label
      }
    >
      <p>{t('Zones:')}</p>
      {accessProfiles
        .find(item => item.value == (userData.Group?._id || userData.Group))
        ?.zones?.map((zone, index) => (
          <p key={index}>{zone.name}</p>
        ))}
    </Popover>
  );

  const renderAccessItem = (label, item) => {
    const speaker = (
      <Popover title={item.label || 'Access Profile'}>
        <p>{t('Zones:')}</p>
        {item.zones?.length > 0 ? (
          item.zones.map((zone, index) => <p key={index}>{zone.name}</p>)
        ) : (
          <p>{t('No accessible zones')}</p>
        )}
      </Popover>
    );

    return (
      <Whisper
        placement="top"
        trigger="hover"
        speaker={speaker}
        controlId={`control-id-${item.value}`}
        key={item.value}
      >
        <div>{label}</div>
      </Whisper>
    );
  };

  return (
    <div>
      {loading ? (
        <Loader backdrop />
      ) : (
        <>
          <Stack
            justifyContent="center"
            alignItems="center"
            direction="column"
            style={{ padding: 20 }}
          >
            <Uploader
              fileListVisible={false}
              listType="picture"
              disabled={userData.forgotten}
              action={`${process.env.REACT_APP_API_URL}User/uploadFiles/`}
              onUpload={file => {
                setUploading(true);
                previewFile(file.blobFile, value => {
                  setFileInfo(value);
                });
              }}
              onSuccess={(response, file) => {
                setUploading(false);
                toaster.push(
                  <Message type="success" showIcon>
                    {t('Uploaded successfully')}
                  </Message>
                );
                changeUser('photo', response.filename);
              }}
              onError={() => {
                setFileInfo(null);
                setUploading(false);
                toaster.push(
                  <Message type="error" showIcon>
                    {t('Upload failed')}
                  </Message>
                );
              }}
            >
              <button style={{ width: 150, height: 150 }}>
                {uploading && <Loader backdrop center />}
                {fileInfo ? (
                  <img src={fileInfo} width="100%" height="100%" />
                ) : user.photo ? (
                  <img
                    src={`${process.env.REACT_APP_IMAGE_URL + user.photo}`}
                    width="100%"
                    height="100%"
                  />
                ) : (
                  <AvatarIcon style={{ fontSize: 80 }} />
                )}
              </button>
            </Uploader>
            <h3 style={{ marginTop: 10 }}>{`${userData?.firstname} ${userData?.lastname}`}</h3>
            <p className="error-page-subtitle text-muted ">{userData?.Email}</p>
            <Toggle
              size="lg"
              checkedChildren="Active"
              unCheckedChildren="Blocked"
              checked={!userData.isBlocked}
              disabled={userData.forgotten}
              onChange={checked => {
                handleBlock(!checked);
              }}
              loading={loading}
            />
          </Stack>
          <Form fluid model={model}>
            <PanelGroup>
              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaRegUser style={{ fontSize: '16px' }} />
                    <span>{t('User Information')}</span>
                  </div>
                }
              >
                <Stack spacing={20} wrap style={{ marginBottom: 20, marginTop: 20 }}>
                  <Form.Group>
                    <Form.ControlLabel>{t('First Name')}</Form.ControlLabel>
                    <Form.Control
                      name="name"
                      style={{ width: 200 }}
                      value={userData?.firstname}
                      disabled={userData.forgotten}
                      onChange={val => {
                        changeUser('firstname', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Last Name')}</Form.ControlLabel>
                    <Form.Control
                      name="name"
                      style={{ width: 200 }}
                      value={userData?.lastname}
                      disabled={userData.forgotten}
                      onChange={val => {
                        changeUser('lastname', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Registration Number')}</Form.ControlLabel>
                    <Form.Control
                      name="name"
                      style={{ width: 200 }}
                      value={userData?.registrationNumber}
                      disabled={userData.forgotten}
                      onChange={val => {
                        changeUser('registrationNumber', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group controlId="email">
                    <Form.ControlLabel>{t('Email')}</Form.ControlLabel>
                    <Form.Control
                      name="email"
                      type="email"
                      style={{ width: 200 }}
                      disabled={userData.forgotten}
                      value={userData?.Email || ''}
                      onChange={val => {
                        setUserData(prev => ({ ...prev, Email: val }));
                        setUpdatedData(prev => ({ ...prev, email: val }));
                      }}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.ControlLabel>{t('Phone')}</Form.ControlLabel>
                    <Form.Control
                      name="phone"
                      type="number"
                      style={{ width: 200 }}
                      disabled={userData.forgotten}
                      value={userData?.telephone}
                      onChange={val => {
                        changeUser('telephone', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Grade')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={grades}
                      style={{ width: 200 }}
                      value={userData?.grade?._id || userData?.grade}
                      disabled={userData.forgotten}
                      onChange={val => {
                        changeUser('grade', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Team')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={servicelines}
                      style={{ width: 200 }}
                      disabled={userData.forgotten}
                      value={userData?.serviceLine?._id || userData?.serviceLine}
                      onChange={val => {
                        changeUser('serviceLine', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Manager')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={mappedManagers}
                      disabled={userData.forgotten}
                      style={{ width: 200 }}
                      value={userData?.manager?._id || userData?.manager}
                      onChange={val => {
                        changeUser('manager', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Second Manager')}</Form.ControlLabel>
                    <SelectPicker
                      placement="auto"
                      data={filteredSecondManagers}
                      disabled={userData.forgotten}
                      style={{ width: 200 }}
                      value={userData?.secondValidator?._id || userData?.secondValidator}
                      onChange={val => {
                        changeUser('secondValidator', val);
                      }}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Hire date')}</Form.ControlLabel>
                    <DatePicker
                      format="dd-MM-yyyy"
                      placement="auto"
                      value={userData.hire_date ? new Date(userData.hire_date) : null}
                      style={{ width: 200 }}
                      disabled={userData.forgotten}
                      onChange={val => {
                        changeUser('hire_date', val);
                      }}
                    />
                  </Form.Group>
                </Stack>
                <Stack spacing={20} wrap style={{ marginBottom: 20, marginTop: 20 }}>
                  <Form.Group>
                    <Form.ControlLabel>{t('Car Numbers')}</Form.ControlLabel>
                    <Stack spacing={10} wrap>
                      <Form.Control
                        name="carNumber"
                        style={{ width: 200 }}
                        value={carNumber}
                        disabled={userData.forgotten}
                        onChange={val => setCarNumber(val)}
                        placeholder={t('Enter car number')}
                      />
                      <Button
                        appearance="primary"
                        disabled={!carNumber || userData.forgotten}
                        onClick={() => {
                          if (carNumber) {
                            const updatedCars = [...(userData.cars || []), carNumber];
                            changeUser('cars', updatedCars);
                            setCarNumber('');
                          }
                        }}
                      >
                        <Plus />
                      </Button>
                    </Stack>
                    <Stack spacing={10} wrap style={{ marginTop: 10 }}>
                      {userData.cars
                        ?.filter(car => car && car.trim() !== '')
                        .map((car, index) => (
                          <div
                            key={index}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                          >
                            <FaCar style={{ fontSize: '16px' }} />
                            <span>{car}</span>
                            <Button
                              appearance="subtle"
                              style={{ color: 'red', fontWeight: 'bold' }}
                              disabled={userData.forgotten}
                              onClick={() => {
                                const updatedCars = userData.cars.filter((_, i) => i !== index);
                                changeUser('cars', updatedCars);
                              }}
                            >
                              X
                            </Button>
                          </div>
                        ))}
                    </Stack>
                  </Form.Group>
                </Stack>
              </Panel>
              {features?.find(item => item.name?.toLowerCase() == 'wfh') && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MdOutlineHomeWork style={{ fontSize: '16px' }} />
                      <span>{t('Remote working')}</span>
                    </div>
                  }
                >
                  <Stack spacing={40} wrap style={{ marginBottom: 20 }}>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      controlId="control-id-hover"
                      speaker={speaker}
                    >
                      <Form.Group>
                        <Form.ControlLabel>{t('WFH Profile')}</Form.ControlLabel>
                        <SelectPicker
                          placement="auto"
                          data={wfhProfiles}
                          style={{ width: 200 }}
                          disabled={userData.forgotten}
                          value={userData?.wfhProfile?._id || userData?.wfhProfile}
                          onChange={val => {
                            changeUser('wfhProfile', val);
                          }}
                          renderMenuItem={(label, item) => renderMenuItem(label, item)}
                        />
                      </Form.Group>
                    </Whisper>
                    <Form.Group>
                      <Form.ControlLabel>{t('WFH Validator')}</Form.ControlLabel>
                      <SelectPicker
                        placement="auto"
                        data={mappedManagers}
                        style={{ width: 200 }}
                        disabled={userData.forgotten}
                        value={userData?.WFH_validator?._id || userData?.WFH_validator}
                        onChange={val => {
                          changeUser('WFH_validator', val);
                        }}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>{t('Seniority balance')}</Form.ControlLabel>
                      <Form.Control
                        name="extraBalance"
                        style={{ width: 200 }}
                        value={extraBalance}
                        disabled={userData.forgotten}
                        onChange={val => setExtraBalance(val)}
                        type="number"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>{t('Part Time')}</Form.ControlLabel>
                      <Form.Control
                        name="partTime"
                        style={{ width: 200 }}
                        value={userData?.partTime}
                        disabled={userData.forgotten}
                        onChange={val => {
                          changeUser('partTime', val);
                        }}
                      />
                    </Form.Group>
                  </Stack>
                </Panel>
              )}
              {features?.find(item => item.name?.toLowerCase() == 'on site') && (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaRegBuilding style={{ fontSize: '16px' }} />
                      <span>{t('Flex office')}</span>
                    </div>
                  }
                >
                  <Stack spacing={40} wrap style={{ marginBottom: 20 }}>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      controlId="control-id-hover"
                      speaker={speakerAccess}
                    >
                      <Form.Group>
                        <Form.ControlLabel>{t('Access profile')}</Form.ControlLabel>
                        <SelectPicker
                          placement="auto"
                          data={accessProfiles}
                          disabled={userData.forgotten}
                          style={{ width: 200 }}
                          value={userData?.Group?._id || userData?.Group}
                          onChange={val => {
                            changeUser('Group', val);
                          }}
                          renderMenuItem={(label, item) => renderAccessItem(label, item)}
                        />
                      </Form.Group>
                    </Whisper>
                    <Form.Group>
                      <Form.ControlLabel>{t('Dedicated desk')}</Form.ControlLabel>
                      <SelectPicker
                        placement="auto"
                        data={desks}
                        disabled={userData.forgotten}
                        style={{ width: 200 }}
                        value={userData?.desk?._id || userData?.desk}
                        onChange={val => {
                          changeUser('desk', val);
                        }}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>{t('Dedicated parking spot')}</Form.ControlLabel>
                      <SelectPicker
                        placement="auto"
                        data={spots}
                        style={{ width: 200 }}
                        disabled={userData.forgotten}
                        value={userData?.spot?._id || userData?.spot}
                        onChange={val => {
                          changeUser('spot', val);
                        }}
                      />
                    </Form.Group>
                  </Stack>
                </Panel>
              )}
              <Panel
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LuUserCog style={{ fontSize: '16px' }} />
                    <span>{t('Roles')}</span>
                  </div>
                }
              >
                <Stack spacing={10}>
                  <Radio
                    value="user"
                    checked={userData.role != 'trainee'}
                    onChange={() => {
                      changeUser('role', 'user');
                    }}
                    disabled={userData.forgotten}
                  >
                    {t('Employee')}
                  </Radio>
                  <Radio
                    value="trainee"
                    checked={userData.role == 'trainee'}
                    disabled={userData.forgotten}
                    onChange={() => {
                      changeUser('role', 'trainee');
                      changeUser('isAdmin', false);
                      changeUser('isManager', false);
                      changeUser('isValidator', false);
                      changeUser('admin', null);
                    }}
                  >
                    {t('Trainee')}
                  </Radio>
                </Stack>
                <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                  <Checkbox
                    onChange={() => {
                      changeUser('isManager', !userData.isManager);
                    }}
                    checked={userData.isManager}
                    disabled={userData.forgotten || userData.role == 'trainee'}
                  >
                    {t('Is Manager')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isValidator', !userData.isValidator);
                    }}
                    checked={userData.isValidator}
                    disabled={userData.forgotten || userData.role == 'trainee'}
                  >
                    {t('Is Validator')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isGFSF', !userData.isGFSF);
                    }}
                    checked={userData.isGFSF}
                    disabled={userData.forgotten || userData.role == 'trainee'}
                  >
                    {t('Is GFSF')}
                  </Checkbox>
                  <Checkbox
                    onChange={() => {
                      changeUser('isSST', !userData.isSST);
                    }}
                    checked={userData.isSST}
                    disabled={userData.forgotten || userData.role == 'trainee'}
                  >
                    {t('Is SST')}
                  </Checkbox>
                </Stack>
                {currentUser?.admin == 'super' && (
                  <Stack spacing={20} wrap style={{ marginBottom: 20 }}>
                    <Checkbox
                      onChange={() => {
                        if (userData.isAdmin) {
                          changeUser('admin', null);
                        }
                        changeUser('isAdmin', !userData.isAdmin);
                      }}
                      checked={userData.isAdmin}
                      disabled={userData.forgotten || userData.role == 'trainee'}
                    >
                      {t('Administrator')}
                    </Checkbox>
                    <Form.Group>
                      <Form.ControlLabel>Assign role</Form.ControlLabel>
                      <InputPicker
                        placement="auto"
                        data={profiles}
                        style={{ width: 200 }}
                        disabled={userData.forgotten || !userData.isAdmin}
                        value={userData.admin}
                        onChange={e => {
                          changeUser('admin', e);
                        }}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.ControlLabel>Module Access</Form.ControlLabel>
                      <TagPicker
                        placement="auto"
                        data={modules}
                        value={userData.moduleAccess}
                        disabled={userData.forgotten}
                        onChange={val => {
                          changeUser('moduleAccess', val);
                        }}
                      />
                    </Form.Group>
                  </Stack>
                )}
              </Panel>
            </PanelGroup>
          </Form>

          <IconButton
            onClick={handleSave}
            appearance="primary"
            icon={<Icon as={VscSave} />}
            style={{ float: 'right' }}
            disabled={userData.forgotten}
          >
            {t('Save')}
          </IconButton>

          <Modal
            open={confirmDialog.isOpen}
            onClose={() => {
              setConfirmDialog({ isOpen: false, title: '', subtitle: '', status: null });
              setResignationDate(null);
            }}
          >
            <Modal.Header>
              <Modal.Title>{confirmDialog.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {confirmDialog.subtitle === true ? (
                <div style={{ lineHeight: '1.5', marginBottom: 10 }}>
                  {t('Are you sure you want to block this user?')}
                  <Form fluid key={`datepicker-${id || 'modal'}`}>
                    <Form.Group>
                      <Form.ControlLabel>{t('Resignation Date')}</Form.ControlLabel>
                      <DatePicker
                        format="dd-MM-yyyy"
                        placement="auto"
                        value={resignationDate}
                        onChange={value => {
                          // console.log('DatePicker changed:', value);
                          setResignationDate(value);
                        }}
                        style={{ width: 200 }}
                        oneTap
                        cleanable
                        key={`datepicker-${id || 'modal'}`}
                      />
                    </Form.Group>
                  </Form>
                </div>
              ) : (
                confirmDialog.subtitle
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={() => {
                  setConfirmDialog({ isOpen: false, title: '', subtitle: '', status: null });
                  setResignationDate(null);
                }}
                appearance="subtle"
              >
                {t('Cancel')}
              </Button>
              <Button onClick={handleUpdateUserStatus} appearance="primary">
                {t('Confirm')}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};

export default UserForm;
